import { useEffect, useState } from 'react';
import { createPixelDataSharedWorker } from 'modules/viewer/workers/PixelDataSharedWorker';
import type { PixelDataSharedWorker } from 'modules/viewer/workers/PixelDataSharedWorker';
import { AppRoutePath } from 'config/constants';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import { atom, useRecoilState } from 'recoil';
import type { RecoilState } from 'recoil';
import { openWindowEvent, dispatchExtensionEvent } from 'domains/extension/extensionEventCreators';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import { env } from 'config/env';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags/suspense';
import { logger } from 'modules/logger';
const terminateSharedWorkerConnection = (worker: PixelDataSharedWorker | null) => {
  if (worker == null) {
    return;
  }

  worker.port.postMessage({
    type: 'terminate-connection',
  });
  try {
    worker.port.close();
  } catch (error) {
    logger.info(
      `failed to close pixel data shared worker port: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};

export const pixelWorkerOpenedState: RecoilState<boolean> = atom({
  key: 'viewer.dre.pixelWorkerOpenState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const useOpenPixelDataSharedWorker = (workerKey: string): PixelDataSharedWorker | null => {
  const [hasWorkerOpened, setWorkerOpened] = useRecoilState(pixelWorkerOpenedState);
  const useDedupedPixelWorker = useFeatureFlagEnabled(FF.PIXEL_WORKER_DEDUPLICATION);
  // VX-4156 kill-switch: when off, the client does not post `enable-pruning` (so the SharedWorker's
  // prune() never marks this port eligible to reclaim) and the watchdog below is disabled.
  // Worker-side prune()/reconcile is unchanged — it cannot read flags — but with no port opted in it
  // has nothing to act on for this connection.
  const enablePortPruning = useFeatureFlagEnabled(FF.VIEWER_SW_PORT_PRUNING);

  useEffect(() => {
    if (!hasWorkerOpened) {
      const isFocused = document.hasFocus();
      if (isWorkspaceManagerAvailable()) {
        const { protocol, host } = window.location;

        const url = `${protocol}//${host}${AppRoutePath.PIXEL_WORKER}`;
        dispatchExtensionEvent(
          openWindowEvent({
            url,
            windowConfig: {
              // `focused` disrupts functionality of `minimized` state
              // see https://issues.chromium.org/issues/40204304
              focused: null,
              top: null,
              left: null,
              width: null,
              height: null,
              state: 'minimized',
            },
          })
        );
        // generally not needed, but a fallback in case the new window somehow steals focus
        if (isFocused) {
          window.focus();
        }
      } else {
        if (env.STORYBOOK === 'true') {
          return;
        }

        window.open(
          AppRoutePath.PIXEL_WORKER,
          AppRoutePath.PIXEL_WORKER,
          'noopener=yes, width=300, height=150, left=0, top=0, toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no'
        );
        if (isFocused) {
          window.focus();
        }
      }

      // Viewer functionality is absolutely dependent on the Shared Worker opening.
      // In the event the dedicated window cannot open, update the state so that
      // the Viewer windows will start talking to the Shared Worker, which will
      // start a new Shared Worker if not already present.
      setTimeout(() => {
        setWorkerOpened(true);
      }, 1500);
    }
  }, [hasWorkerOpened, setWorkerOpened]);

  const [pixelDataSharedWorkerMap, setPixelDataSharedWorkerMap] = useState<{
    [caseSmid: string]: PixelDataSharedWorker | null;
  }>({ '': null });

  const matchingWorker = pixelDataSharedWorkerMap[workerKey] ?? null;

  useEffect(() => {
    if (hasWorkerOpened && matchingWorker == null) {
      setPixelDataSharedWorkerMap({
        [workerKey]: createPixelDataSharedWorker(
          useDedupedPixelWorker,
          undefined,
          enablePortPruning
        ),
      });
      logger.info(`new shared worker connection for ${workerKey}`);
    }
  }, [hasWorkerOpened, useDedupedPixelWorker, enablePortPruning, matchingWorker, workerKey]);

  // cancel the current connection if it is replaced
  useEffect(() => {
    return () => {
      terminateSharedWorkerConnection(matchingWorker);
    };
  }, [matchingWorker]);

  // VX-4156: ping/pong watchdog. If two consecutive pings receive no pong, null out the matching
  // worker so the existing effect recreates it. Gated by the VIEWER_SW_PORT_PRUNING kill-switch:
  // when off, the watchdog never arms and the worker is never recreated on a missed pong.
  useEffect(() => {
    if (!enablePortPruning || matchingWorker == null) return;

    let missedPongCount = 0;
    let pendingPongTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const onMessage = (e: MessageEvent) => {
      if ((e as MessageEvent<{ type?: string }>).data?.type === 'pong') {
        if (pendingPongTimeoutId != null) {
          clearTimeout(pendingPongTimeoutId);
          pendingPongTimeoutId = null;
        }
        missedPongCount = 0;
      }
    };
    matchingWorker.port.addEventListener('message', onMessage);

    // VX-4223: a pong timeout armed while hidden can fire just after the tab becomes visible —
    // its lateness is background timer throttling, not a severed port. On the hidden->visible
    // transition, discard that stale timeout and reset the miss count so recovery requires fresh
    // consecutive misses observed entirely while visible (the in-callback guard below covers
    // timeouts that fire while still hidden).
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (pendingPongTimeoutId != null) {
        clearTimeout(pendingPongTimeoutId);
        pendingPongTimeoutId = null;
      }
      missedPongCount = 0;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const pingIntervalId = setInterval(() => {
      try {
        matchingWorker.port.postMessage({ type: 'ping' });
      } catch {
        // port may be closed mid-cycle — treat as a missed pong
      }
      pendingPongTimeoutId = setTimeout(() => {
        // VX-4223: a hidden tab has throttled/frozen timers, so a late pong is expected and
        // not a sign of a severed port. Don't count a miss while hidden, and clear any miss
        // accrued before hiding so a stale pre-hide miss can't combine with a post-unhide one
        // to recreate a still-healthy connection. Require fresh consecutive misses once visible.
        if (document.visibilityState === 'hidden') {
          missedPongCount = 0;
          return;
        }
        missedPongCount += 1;
        if (missedPongCount >= 2) {
          logger.warn(`SW port stale for workerKey=${workerKey}; recreating worker`);
          terminateSharedWorkerConnection(matchingWorker);
          // Null the worker out before the create effect rebuilds it. This null transition is
          // load-bearing: useReinitializeProvidersOnWorkerChange treats oldWorker -> null ->
          // newWorker as the recovery signal that rebinds existing providers to the new port. A
          // rebuild that skips null would look like referential churn and the page would silently
          // stop self-healing (VX-4223).
          setPixelDataSharedWorkerMap({ [workerKey]: null });
        }
      }, 10_000);
    }, 30_000);

    return () => {
      clearInterval(pingIntervalId);
      if (pendingPongTimeoutId != null) clearTimeout(pendingPongTimeoutId);
      matchingWorker.port.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enablePortPruning, matchingWorker, workerKey]);

  // cancel the page's current connection as the page closes
  useEffect(() => {
    function terminateConnection() {
      terminateSharedWorkerConnection(matchingWorker);
    }

    window.addEventListener('beforeunload', terminateConnection);
    return () => window.removeEventListener('beforeunload', terminateConnection);
  }, [matchingWorker]);

  return matchingWorker;
};
